import type { Scene } from '../core/hp/types';
import { copyProjectView, readProjectView, removeProjectView, type ProjectView } from './projectView';
import { projectPreviews } from './projectPreview';
import { projectFloorArea } from '../core/geometry/roomFloor';
import { folderOf } from './projectFolders';
import { t } from '../i18n/I18n';

export interface StoredProject { id: string; name: string; scene: Scene; savedAt: number }
export interface PlanThumbnailData {
  viewBox: string;
  rooms: string[];
  walls: { x: number; z: number; ex: number; ez: number; thickness: number }[];
  items: { x: number; z: number; w: number; d: number; rotation: number }[];
}
export interface LocalProject {
  id: string; name: string; savedAt: number; area: number;
  thumbnail: PlanThumbnailData | null;
  /** Where the project was left; the card shows its frame and its label. */
  view: ProjectView | null;
  /** The folder it is filed in: its room type, as the app files its projects (projectFolders.ts). */
  folder: string;
}
/** A project in «Недавно удалённые»: when it went there, and the whole days it has left there. */
export interface TrashedProject extends LocalProject { deletedAt: number; daysLeft: number }
const prefix = 'hp.scene.';
/**
 * A deleted project waits in «Недавно удалённые» for the app's 14 days (MyProjectsStorage.DaysToKeepInTrash) before it
 * is deleted for good. Its record stays where it is and `hp.trash.<id>` marks it with the time it went: a few bytes,
 * so a browser whose storage is full can still delete, and a link to it opens nothing until it is restored.
 */
const trashPrefix = 'hp.trash.';
export const DAYS_TO_KEEP_IN_TRASH = 14;
const DAY = 86_400_000;
export function readProject(id: string): StoredProject {
  const value = JSON.parse(localStorage.getItem(prefix + id) || 'null');
  if (!Array.isArray(value?.scene?.levels) || typeof value.savedAt !== 'number') throw new Error(t('#LibraryProjectNotFound'));
  return { id, name: value.name || t('#LibraryDefaultName'), savedAt: value.savedAt, scene: value.scene };
}
/** The projects of this browser, «Недавно удалённые» left out. */
export function localProjects(): LocalProject[] {
  const trashed = trashMarks();
  const projects: LocalProject[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix) || trashed.has(key.slice(prefix.length))) continue;
    try { projects.push(projectCard(readProject(key.slice(prefix.length)))); } catch { /* An unreadable snapshot remains on disk for recovery. */ }
  }
  return projects.sort((a, b) => b.savedAt - a.savedAt);
}
function projectCard(project: StoredProject): LocalProject {
  const view = readProjectView(project.id);
  return { id: project.id, name: project.name, savedAt: project.savedAt, area: projectFloorArea(project.scene.levels),
    thumbnail: thumbnailData(project.scene, view?.levelIndex ?? 0), view, folder: folderOf(project.scene.roomType) };
}
/** «Недавно удалённые», the latest deleted first, as the app lists them. */
export function trashedProjects(now = Date.now()): TrashedProject[] {
  const projects: TrashedProject[] = [];
  for (const [id, deletedAt] of trashMarks()) {
    try { projects.push({ ...projectCard(readProject(id)), deletedAt, daysLeft: daysLeftInTrash(deletedAt, now) }); } catch { /* purgeTrash drops a mark whose record went. */ }
  }
  return projects.sort((a, b) => b.deletedAt - a.deletedAt);
}
/** When each project in «Недавно удалённые» went there; an unreadable mark counts from now rather than from long ago. */
function trashMarks(): Map<string, number> {
  const marks = new Map<string, number>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(trashPrefix)) continue;
    const deletedAt = Number(localStorage.getItem(key));
    marks.set(key.slice(trashPrefix.length), Number.isFinite(deletedAt) && deletedAt > 0 ? deletedAt : Date.now());
  }
  return marks;
}
/** The whole days a project deleted at `deletedAt` has left there: the app counts the days gone by whole (TimeSpan.Days). */
export function daysLeftInTrash(deletedAt: number, now = Date.now()): number {
  return Math.max(DAYS_TO_KEEP_IN_TRASH - Math.floor((now - deletedAt) / DAY), 0);
}
export function isTrashed(id: string): boolean { return localStorage.getItem(trashPrefix + id) !== null; }
/** To «Недавно удалённые» (the app's DeleteProject → MyProjectsStorage.DeleteToTrash). */
export function trashProjects(ids: readonly string[], now = Date.now()): void {
  for (const id of ids) localStorage.setItem(trashPrefix + id, String(now));
}
/** Back from «Недавно удалённые» into their folders (RestoreMyProject → MyProjectsStorage.RestoreFromTrash). */
export function restoreProjects(ids: readonly string[]): void {
  for (const id of ids) localStorage.removeItem(trashPrefix + id);
}
/** «Удалить навсегда»: the project, its view and its frame go, and its mark with them. */
export function deleteProjectsForever(ids: readonly string[]): void {
  for (const id of ids) { removeProject(id); localStorage.removeItem(trashPrefix + id); }
}
/**
 * What has been in «Недавно удалённые» more whole days than the app keeps it (MyProjectsStorage.IsTrashExpired) is
 * deleted for good when the projects are listed, as the app does when it loads them; so is a mark whose project went.
 */
export function purgeTrash(now = Date.now()): void {
  for (const [id, deletedAt] of trashMarks()) {
    if (Math.floor((now - deletedAt) / DAY) > DAYS_TO_KEEP_IN_TRASH || localStorage.getItem(prefix + id) === null) deleteProjectsForever([id]);
  }
}
/**
 * Files projects in a folder: sets their room type, as the app's MoveRoomToFolder does, and counts as a change of each
 * (the app's UpdatedUtc). A project already in the folder is left as it is. How many moved.
 */
export function moveProjectsToFolder(ids: readonly string[], folder: string, now = Date.now()): number {
  let moved = 0;
  for (const id of ids) {
    const project = readProject(id);
    if (folderOf(project.scene.roomType) === folder) continue;
    writeProject({ ...project, savedAt: now, scene: { ...project.scene, roomType: folder } });
    moved++;
  }
  return moved;
}
export function writeProject(project: StoredProject): void {
  localStorage.setItem(prefix + project.id, JSON.stringify({ scene: project.scene, name: project.name, savedAt: project.savedAt }));
}
export function removeProject(id: string): void {
  localStorage.removeItem(prefix + id);
  removeProjectView(id);
  void projectPreviews.remove(id);
}
export function duplicateProject(project: Pick<LocalProject, 'id'>): StoredProject {
  const original = readProject(project.id);
  const copy = { ...original, id: 'local-' + crypto.randomUUID(), name: t('#LibraryCopyName', original.name), savedAt: Date.now() };
  writeProject(copy);
  // The copy opens on the same view, and its card shows the same frame until it is left on one of its own.
  copyProjectView(original.id, copy.id);
  void projectPreviews.copy(original.id, copy.id);
  return copy;
}
/** The drawn plan behind the stored frame — of the floor the project was left on. */
function thumbnailData(scene: Scene, levelIndex: number): PlanThumbnailData | null {
  const level = scene.levels[levelIndex]?.walls.length ? scene.levels[levelIndex] : scene.levels[0];
  if (!level?.walls.length) return null;
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (const wall of level.walls) for (const p of [wall.start, wall.end]) {
    minX = Math.min(minX, p.x); minZ = Math.min(minZ, p.z); maxX = Math.max(maxX, p.x); maxZ = Math.max(maxZ, p.z);
  }
  const width = maxX - minX, depth = maxZ - minZ, margin = Math.max(width, depth) * .15 + .2;
  const rooms: string[] = []; let remaining = 1024;
  for (const room of level.rooms) {
    if (room.polygon.length > remaining) continue;
    rooms.push(room.polygon.map(p => p.x + ',' + p.z).join(' ')); remaining -= room.polygon.length;
  }
  return { viewBox: [minX - margin, minZ - margin, width + margin * 2, depth + margin * 2].join(' '), rooms,
    walls: level.walls.slice(0, 512).map(wall => ({ x: wall.start.x, z: wall.start.z, ex: wall.end.x, ez: wall.end.z, thickness: wall.thickness })),
    items: level.items.filter(item => item.placement === 'floor').slice(0, 128).map(item => ({ x: item.position.x, z: item.position.z,
      w: item.size.w, d: item.size.d, rotation: item.rotation })) };
}
